import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  AfterViewChecked,
  ChangeDetectorRef,
  DestroyRef,
} from '@angular/core';
import { MenuKey } from '@enums/menu.enum';
import { select, Store } from '@ngrx/store';
import { FormControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { filter, merge, Observable, of, switchMap, take } from 'rxjs';
import * as SearchConfigurationSelectors from '@states/camera-edit/camera-edit.selectors';
import * as SearchConfigurationActions from '@states/camera-edit/camera-edit.actions';
import { SearchConfigurationEffect } from '@effects/search-configuration.effect';
import { CamerasActions } from '@states/cameras/cameras.action-types';
import { CameraActions } from '@states/camera/camera.action-types';
import { LocationsService } from 'src/app/locations/locations.service';
import { UiSchedule } from '@models/alerts-v2.model';
import _ from 'lodash';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { ExternalStorageActions } from '@states/external-storage/external-storage.action-types';
import { ExternalStorageModels } from '@models/external-storage.models';
import { ExternalStorageSelectors } from '@states/external-storage/external-storage.selector-types';
import { MenuActions } from '@states/menu/menu.action-types';
import { SharedActions } from '@states/shared/shared.action-types';
import { CameraStreamDocument, EdgeCamera } from '../../../../../../../../cameras/camera.model';
import { AppState } from '../../../../../../../../reducers';
import { SharedService } from '../../../../../../../../development/shared.service';
import { LetDirective } from '@ngrx/component';
import { TranslatePipe } from '@ngx-translate/core';
import { MatOption, MatSelect } from '@angular/material/select';
import { UiScheduleComponent } from '../../../../../../../../shared/ui-kit/ui-schedule/ui-schedule.component';
import { NgForOf, NgIf } from '@angular/common';
import { IfDeveloperDirective } from '../../../../../../../../directives/if-developer.directive';
import { UiToggleComponent } from '@ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface CameraStorageSettings {
  storageStream: string;
  maxDaysEnabled: boolean;
  maxGbEnabled: boolean;
  storageMaxDays: number;
  storageMaxGB: number;
  storageSchedule: UiSchedule[];
  smartEnabled: boolean;
  preEventDuration: number;
  postEventDuration: number;
}

enum StorageType {
  Cloud,
  External
}

@Component({
  selector: 'app-camera-storage-settings-tab',
  templateUrl: './camera-storage-settings-tab-v2.component.html',
  styleUrls: ['./camera-storage-settings-tab-v2.component.scss'],
  imports: [LetDirective, ReactiveFormsModule, TranslatePipe, MatSelect, MatOption, UiScheduleComponent, NgIf, UiToggleComponent, NgForOf, IfDeveloperDirective],
})
export class CameraStorageSettingsTabComponentV2 implements OnInit, OnDestroy, AfterViewChecked {

  @ViewChild('wrapper') wrapper: ElementRef;

  @Input() standalone = false;

  @Output() onStorageSettingsChange = new EventEmitter<void>();

  public selectExternalStorageList$: Observable<ExternalStorageModels.ExternalStorageDocument[]> = this.store$.pipe(select(ExternalStorageSelectors.selectExternalStorageList));
  public selectSelectedCamera$: Observable<EdgeCamera.CameraItem> = this.store$.pipe(
    select(SearchConfigurationSelectors.selectSelectedCamera),
  );

  public selectIsDeveloper$: Observable<boolean> = this.store$.pipe(select(OrganizationSelectors.isDeveloper));

  public storageTypes = StorageType;
  public storage = new UntypedFormGroup({
    storageEnabled: new UntypedFormControl(true),
    storageStream: new UntypedFormControl('0'),
    maxDaysEnabled: new UntypedFormControl(true),
    maxGbEnabled: new UntypedFormControl(true),
    storageMaxDays: new UntypedFormControl(30),
    retentionDays: new UntypedFormControl(30),
    snapshotRetentionDays: new UntypedFormControl(3),
    storageMaxGB: new UntypedFormControl('', [Validators.min(0), Validators.max(1024)]),
    smartEnabled: new UntypedFormControl(false),
    preEventDuration: new UntypedFormControl(0, [Validators.min(1), Validators.max(30)]),
    postEventDuration: new UntypedFormControl(0, [Validators.min(1), Validators.max(30)]),
    storageSchedule: new UntypedFormControl(),
    additionalEnabled: new UntypedFormControl(false),
  });

  public cloudStorage = new UntypedFormGroup({
    enabled: new UntypedFormControl(false),
    cloudRetentionPeriod: new UntypedFormControl(30),
    backupStorage: new UntypedFormControl(true),
    backupAlerts: new UntypedFormControl(true),
    backupEventTypes: new UntypedFormControl(false),
    footageSchedule: new UntypedFormControl(),
    uploadSchedule: new UntypedFormControl(),
    storageSchedule: new UntypedFormControl(),
  });

  public externalStorage = new UntypedFormGroup({
    enabled: new UntypedFormControl(false),
    externalRetentionPeriod: new UntypedFormControl(30),
    backupStorage: new UntypedFormControl(true),
    backupAlerts: new UntypedFormControl(true),
    backupEventTypes: new UntypedFormControl(false),
    footageSchedule: new UntypedFormControl(),
    uploadSchedule: new UntypedFormControl(),
    storageSchedule: new UntypedFormControl(),
    externalStorageId: new UntypedFormControl(),
  });
  public additionalStorageTypeControl: FormControl = new FormControl();
  public subStreams: CameraStreamDocument[] = [];

  public updatedStorage: any = {};

  private previousHeight = 0;

  constructor(
    private store$: Store<AppState>,
    private sharedService: SharedService,
    private editCameraEffects: SearchConfigurationEffect,
    private locationsService: LocationsService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
  ) {
  }

  public get wrapperHeight() {
    if (this.wrapper?.nativeElement && this.standalone) {
      return this.wrapper.nativeElement.scrollHeight;
    }
    return 600; // Default height
  }

  public get backupStorage() {
    return this.cloudStorage.get('backupStorage').value;
  }

  public get backupStorageExternal() {
    return this.externalStorage.get('backupStorage').value;
  }

  public get subStreamNums() {
    return this.subStreams?.map((subStream, idx) => idx);
  }

  public get footageSchedule() {
    switch (this.additionalStorageType) {
      case StorageType.Cloud:
        return this.cloudStorage.get('footageSchedule').value;
      case StorageType.External:
        return this.externalStorage.get('footageSchedule').value;
    }
  }

  public get uploadSchedule() {
    switch (this.additionalStorageType) {
      case StorageType.Cloud:
        return this.cloudStorage.get('uploadSchedule').value;
      case StorageType.External:
        return this.externalStorage.get('uploadSchedule').value;
    }
  }

  public get storageSchedule() {
    return this.storage.get('storageSchedule').value;
  }

  public get smartEnabled() {
    return this.storage.get('smartEnabled').value;
  }

  public get cloudEnabled() {
    return this.cloudStorage.get('enabled').value && this.storage.get('additionalEnabled')?.value;
  }

  get isMaxDaysEnabled() {
    return this.storage.get('maxDaysEnabled').value;
  }

  get isMaxGbEnabled() {
    return this.storage.get('maxGbEnabled').value;
  }

  public get externalEnabled() {
    if (this.standalone) {
      // In standalone mode, show external storage if external type is selected
      // regardless of additionalEnabled value, as long as it's not explicitly false
      return this.externalStorage.get('enabled').value &&
        this.storage.get('additionalEnabled')?.value !== false;
    }
    return this.externalStorage.get('enabled').value && this.storage.get('additionalEnabled')?.value;
  }

  public get additionalStorageType() {
    return this.additionalStorageTypeControl.value;
  }

  ngAfterViewChecked(): void {
    if (this.standalone && this.wrapper?.nativeElement) {
      const currentHeight = this.wrapper.nativeElement.scrollHeight;
      if (currentHeight !== this.previousHeight) {
        this.previousHeight = currentHeight;
        // Trigger change detection to update parent binding
        this.cdr.detectChanges();
      }
    }
  }

  setSmartStorage() {
    const val = this.storage.get('storageStream').value;
    // If main stream - disable all, otherwise, enable all
    if (val === '0') {
      this.disableSelection();
      this.storage.get('smartEnabled')
        .setValue(false);
    } else if (val === '1') {
      this.enableSelection();
      this.storage.get('smartEnabled')
        .setValue(true);
    } else {
      this.storage.get('smartEnabled')
        .setValue(null);
    }
  }

  ngOnInit(): void {
    this.store$.dispatch(ExternalStorageActions.resetToInitialState());
    this.store$.dispatch(ExternalStorageActions.getExternalStorage());

    // Only set menu when not in standalone mode
    if (!this.standalone) {
      this.store$.dispatch(
        MenuActions.setActiveLevel2Menu({
          activeLevel2: MenuKey.storageSettings,
        }),
      );
    }

    // Initialize empty forms for standalone mode
    if (this.standalone) {
      this.initializeEmptyForms();
    }

    this.additionalStorageTypeControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        if (res === StorageType.Cloud) {
          this.cloudStorage.patchValue({ enabled: true });
          this.externalStorage.patchValue({ enabled: false });
        } else if (res === StorageType.External) {
          this.cloudStorage.patchValue({ enabled: false });
          this.externalStorage.patchValue({ enabled: true });
        } else {
          this.cloudStorage.patchValue({ enabled: false });
          this.externalStorage.patchValue({ enabled: false });
        }
      });

    this.disableSelection();
    this.setupFormListeners();

    // Only fetch camera data when not in standalone mode
    if (!this.standalone) {
      this.selectSelectedCamera$.pipe(take(1))
        .pipe(
          switchMap(camera => {
            return this.locationsService.getCameraById(camera.locationId, camera.edgeId, camera?.edgeOnly?.cameraId ?? camera.cameraId)
              .pipe(
                switchMap(res => {
                  const cam = _.cloneDeep(camera);
                  let _res = _.cloneDeep(res);
                  _res = _.merge(cam, _res);
                  this.store$.dispatch(CameraActions.GetLocationEdgesCamerasSuccess({ payload: [_res] }));
                  this.store$.dispatch(SearchConfigurationActions.setSelectedCamera({ selectedCamera: _res }));
                  return of();
                }));
          }))
        .subscribe();

      this.selectSelectedCamera$
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          filter(camera => !!camera),
        )
        .subscribe(camera => {
          this.populateFormWithCameraData(camera);
        });
    }

    this.setupFormValueChanges();

    this.editCameraEffects.pressSave$.pipe(
      takeUntilDestroyed(this.destroyRef),
      filter(() => !this.standalone), // Only process save events when not in standalone mode
    )
      .subscribe(() => {
        this.updatedStorage.cloudStorage.enabled = this.cloudEnabled ?? false;
        this.store$.dispatch(
          SearchConfigurationActions.cameraStorageUpdate({
            data: this.updatedStorage,
          }),
        );
      });

    this.addModifiedFieldStyles();
  }

  public updateFootageSchedule(schedule: UiSchedule[]) {
    switch (this.additionalStorageType) {
      case StorageType.Cloud:
        this.cloudStorage.get('footageSchedule')
          .setValue(schedule);
        break;
      case StorageType.External:
        this.externalStorage.get('footageSchedule')
          .setValue(schedule);
        break;
    }
  }

  public updateUploadSchedule(schedule: UiSchedule[]) {
    switch (this.additionalStorageType) {
      case StorageType.Cloud:
        this.cloudStorage.get('uploadSchedule')
          .setValue(schedule);
        break;
      case StorageType.External:
        this.externalStorage.get('uploadSchedule')
          .setValue(schedule);
        break;
    }
  }

  public updateStorageSchedule(schedule: UiSchedule[]) {
    this.storage.get('storageSchedule')
      .setValue(schedule);
  }

  public updateExternalStorageSchedule(schedule: UiSchedule[]) {
    switch (this.additionalStorageType) {
      case StorageType.Cloud:
        this.cloudStorage.get('storageSchedule')
          .setValue(schedule);
        break;
      case StorageType.External:
        this.externalStorage.get('storageSchedule')
          .setValue(schedule);
        break;
    }
  }

  public ngOnDestroy(): void {
    this.store$.dispatch(SharedActions.setIsSaving({ isSaving: false }));
  }

  private disableSelection() {
    this.storage.get('smartEnabled')
      .disable();
  }

  private enableSelection() {
    this.storage.get('smartEnabled')
      .enable();
  }

  private addModifiedFieldStyles() {
    if (!this.standalone) return;

    // Track changes for storage form controls
    Object.keys(this.storage.controls).forEach(key => {
      const control = this.storage.get(key);
      control?.valueChanges.subscribe(value => {
        const element = document.querySelector(`[formControlName="${key}"]`);
        if (element) {
          if (value !== null && value !== '') {
            element.classList.add('modified-field');
          } else {
            element.classList.remove('modified-field');
          }
        }
      });
    });

    // Track changes for cloud storage form controls
    Object.keys(this.cloudStorage.controls).forEach(key => {
      const control = this.cloudStorage.get(key);
      control?.valueChanges.subscribe(value => {
        const element = document.querySelector(`[formControlName="${key}"]`);
        if (element) {
          if (value !== null && value !== '') {
            element.classList.add('modified-field');
          } else {
            element.classList.remove('modified-field');
          }
        }
      });
    });

    // Track changes for external storage form controls
    Object.keys(this.externalStorage.controls).forEach(key => {
      const control = this.externalStorage.get(key);
      control?.valueChanges.subscribe(value => {
        const element = document.querySelector(`[formControlName="${key}"]`);
        if (element) {
          if (value !== null && value !== '') {
            element.classList.add('modified-field');
          } else {
            element.classList.remove('modified-field');
          }
        }
      });
    });

    // Track changes for additionalStorageTypeControl (standalone FormControl)
    this.additionalStorageTypeControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      setTimeout(() => {
        const element = document.querySelector('[data-track-changes="additionalStorageType"]');
        if (element) {
          if (value !== null && value !== '') {
            element.classList.add('modified-field');
          } else {
            element.classList.remove('modified-field');
          }
        }
      }, 50);
    });

    // Track changes for ui-schedule components using data attributes
    setTimeout(() => {
      // Track cloud storage footage schedule
      this.cloudStorage.get('footageSchedule')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
        const element = document.querySelector('[data-track-changes="footageSchedule"]');
        if (element && this.cloudEnabled) {
          if (value && value.length > 0) {
            element.classList.add('modified-field');
          } else {
            element.classList.remove('modified-field');
          }
        }
      });

      // Track external storage footage schedule
      this.externalStorage.get('footageSchedule')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
        const element = document.querySelector('[data-track-changes="footageScheduleExternal"]');
        if (element && this.externalEnabled) {
          if (value && value.length > 0) {
            element.classList.add('modified-field');
          } else {
            element.classList.remove('modified-field');
          }
        }
      });

      // Track cloud storage upload schedule
      this.cloudStorage.get('uploadSchedule')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
        const element = document.querySelector('[data-track-changes="uploadSchedule"]');
        if (element && this.cloudEnabled) {
          if (value && value.length > 0) {
            element.classList.add('modified-field');
          } else {
            element.classList.remove('modified-field');
          }
        }
      });

      // Track external storage upload schedule
      this.externalStorage.get('uploadSchedule')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
        const element = document.querySelector('[data-track-changes="uploadScheduleExternal"]');
        if (element && this.externalEnabled) {
          if (value && value.length > 0) {
            element.classList.add('modified-field');
          } else {
            element.classList.remove('modified-field');
          }
        }
      });
    }, 100);
  }

  private initializeEmptyForms(): void {
    // Initialize empty forms for standalone mode
    this.storage.patchValue({
      storageEnabled: null,
      maxDaysEnabled: '',
      maxGbEnabled: '',
      storageMaxGB: '',
      storageMaxDays: '',
      retentionDays: '',
      snapshotRetentionDays: '',
      smartEnabled: '',
      preEventDuration: '',
      postEventDuration: '',
      storageStream: '',
      additionalEnabled: '',
      storageSchedule: [],
    });

    this.cloudStorage.patchValue({
      enabled: false,
      cloudRetentionPeriod: 30,
      backupStorage: true,
      backupAlerts: true,
      backupEventTypes: false,
      footageSchedule: [],
      uploadSchedule: [],
      storageSchedule: [],
    });

    this.externalStorage.patchValue({
      enabled: false,
      externalRetentionPeriod: 30,
      backupStorage: true,
      backupAlerts: true,
      backupEventTypes: false,
      footageSchedule: [],
      uploadSchedule: [],
      storageSchedule: [],
      externalStorageId: null,
    });
  }

  private setupFormListeners(): void {
    this.storage.get('storageStream')
      .valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (val) => {
          if (val === '0') {
            this.disableSelection();
          } else if (val === '1') {
            this.enableSelection();
          } else {
            this.disableSelection();

          }
        });

    this.storage.get('maxDaysEnabled')
      .valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        if (val) {
          this.storage.get('storageMaxDays')
            .enable();
        } else {
          this.storage.get('storageMaxDays')
            .setValue(30);
        }
      });

    this.storage.get('maxGbEnabled')
      .valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        if (val) {
          this.storage.get('storageMaxGB')
            .enable();
        } else {
          this.storage.get('storageMaxGB')
            .setValue(0);
        }
      });

    this.storage.get('smartEnabled')
      .valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        if (val) {
          this.storage.get('preEventDuration')
            .enable();
          this.storage.get('postEventDuration')
            .enable();
        } else {
          this.storage.get('preEventDuration')
            .disable();
          this.storage.get('postEventDuration')
            .disable();
        }
      });
  }

  private populateFormWithCameraData(camera: EdgeCamera.CameraItem): void {
    const edgeOnly = camera.edgeOnly;
    this.store$.dispatch(CamerasActions.setCameraDetails({ cameraDetails: edgeOnly }));
    this.store$.dispatch(CamerasActions.setLocationId({ locationId: camera.locationId }));
    this.store$.dispatch(CamerasActions.setEdgeId({ edgeId: camera.edgeId }));
    this.subStreams = edgeOnly?.subStreams;

    // Populate form values from camera data
    this.storage.patchValue({
      storageEnabled: edgeOnly?.storageEnabled ?? true,
      maxDaysEnabled: !!edgeOnly?.storageMaxDays,
      maxGbEnabled: !!edgeOnly?.storageMaxGB,
      storageMaxGB: edgeOnly?.storageMaxGB,
      storageMaxDays: edgeOnly?.storageMaxDays ?? 30,
      retentionDays: edgeOnly?.retentionDays ?? 30,
      snapshotRetentionDays: edgeOnly?.snapshotRetentionDays ?? 3,
      smartEnabled: !!edgeOnly?.smartStorage?.enabled,
      preEventDuration: edgeOnly?.smartStorage?.preEventDuration,
      storageSchedule: edgeOnly?.storageSchedule,
      postEventDuration: edgeOnly?.smartStorage?.postEventDuration,
      storageStream: edgeOnly?.storageStream?.storageFromSubstream ? `${edgeOnly?.storageStream?.storageSubstreamIndex + 1}` : '0',
      additionalEnabled: edgeOnly?.additionalEnabled,
    });

    // Populate cloud storage form
    this.cloudStorage.patchValue({
      // @ts-ignore
      enabled: !!edgeOnly?.cloudStorage?.enabled,
      cloudRetentionPeriod: edgeOnly?.cloudStorage?.cloudRetentionPeriod ?? 30,
      backupStorage: edgeOnly?.cloudStorage?.enabled ? !!edgeOnly?.cloudStorage?.backupStorage : true,
      backupAlerts: edgeOnly?.cloudStorage?.enabled ? !!edgeOnly?.cloudStorage?.backupAlerts : true,
      backupEventTypes: !!edgeOnly?.cloudStorage?.backupEventTypes,
      footageSchedule: edgeOnly?.cloudStorage?.footageSchedule,
      uploadSchedule: edgeOnly?.cloudStorage?.uploadSchedule,
    });

    // Populate external storage form
    this.externalStorage.patchValue({
      enabled: !!edgeOnly?.externalStorage?.enabled,
      externalRetentionPeriod: edgeOnly?.externalStorage?.externalRetentionPeriod ?? 30,
      backupStorage: edgeOnly?.externalStorage?.enabled ? !!edgeOnly?.externalStorage?.backupStorage : true,
      backupAlerts: edgeOnly?.externalStorage?.enabled ? !!edgeOnly?.externalStorage?.backupAlerts : true,
      backupEventTypes: !!edgeOnly?.externalStorage?.backupEventTypes,
      footageSchedule: edgeOnly?.externalStorage?.footageSchedule,
      uploadSchedule: edgeOnly?.externalStorage?.uploadSchedule,
      externalStorageId: edgeOnly?.externalStorage?.externalStorageId,
    });

    // Set the correct storage type radio
    if (!!edgeOnly?.cloudStorage?.enabled) {
      this.additionalStorageTypeControl.setValue(StorageType.Cloud);
    }
    if (!!edgeOnly?.externalStorage?.enabled) {
      this.additionalStorageTypeControl.setValue(StorageType.External);
    }
  }

  private setupFormValueChanges(): void {
    merge(this.storage.valueChanges, this.cloudStorage.valueChanges, this.externalStorage.valueChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(_ => {
        const dirty: Partial<CameraStorageSettings> = this.sharedService.getDirtyValues(this.storage);

        if (this.standalone) {
          this.updatedStorage = {
            ...((this.storage?.get('storageEnabled')?.value !== null && this.storage?.get('storageEnabled')?.value !== '') && { storageEnabled: this.storage?.get('storageEnabled')?.value }),
            ...(this.storage?.get('retentionDays')?.value !== '' && { retentionDays: this.storage?.get('retentionDays')?.value }),
            ...(this.storage?.get('snapshotRetentionDays')?.value !== '' && { snapshotRetentionDays: this.storage?.get('snapshotRetentionDays')?.value }),
            ...(this.storage?.get('storageMaxDays')?.value !== '' && { storageMaxDays: this.storage?.get('storageMaxDays')?.value }),
            ...(this.storage?.get('storageMaxGB')?.value !== '' && { storageMaxGB: this.storage?.get('storageMaxGB')?.value }),
            ...(this.storage?.get('storageSchedule')?.value && { storageSchedule: this.storage?.get('storageSchedule')?.value }),
            ...((this.storage?.get('smartEnabled')?.value !== null && this.storage?.get('smartEnabled')?.value !== '') && {
              smartStorage: {
                enabled: this.smartEnabled,
                ...(this.storage?.get('preEventDuration')?.value !== '' && { preEventDuration: this.storage?.get('preEventDuration')?.value }),
                ...(this.storage?.get('postEventDuration')?.value !== '' && { postEventDuration: this.storage?.get('postEventDuration')?.value }),
              },
            }),
            ...((this.storage?.get('storageStream')?.value !== null && this.storage?.get('storageStream')?.value !== '') && {
              storageStream: {
                storageFromSubstream: this.storage?.get('storageStream')?.value === '0' ? false : true,
                storageSubstreamIndex: this.storage?.get('storageStream')?.value === '0' ? 0 : Number(this.storage?.get('storageStream')?.value) - 1,
              },
            }),
            ...(this.cloudStorage?.get('enabled')?.value !== '' && {
              cloudStorage: {
                enabled: this.cloudEnabled ?? false,
                ...(this.cloudStorage?.get('cloudRetentionPeriod')?.value !== '' && { cloudRetentionPeriod: this.cloudStorage?.get('cloudRetentionPeriod')?.value }),
                ...(this.cloudStorage?.get('backupStorage')?.value !== '' && { backupStorage: this.cloudStorage?.get('backupStorage')?.value }),
                ...(this.cloudStorage?.get('backupAlerts')?.value !== '' && { backupAlerts: this.cloudStorage?.get('backupAlerts')?.value }),
                ...(this.cloudStorage?.get('backupEventTypes')?.value !== '' && { backupEventTypes: this.cloudStorage?.get('backupEventTypes')?.value }),
                ...(this.cloudStorage?.get('footageSchedule')?.value && { footageSchedule: this.cloudStorage?.get('footageSchedule')?.value }),
                ...(this.cloudStorage?.get('uploadSchedule')?.value && { uploadSchedule: this.cloudStorage?.get('uploadSchedule')?.value }),
              },
            }),
            ...(this.externalStorage?.get('enabled')?.value !== '' && {
              externalStorage: {
                enabled: this.externalEnabled ?? false,
                ...(this.externalStorage?.get('externalRetentionPeriod')?.value !== '' && { externalRetentionPeriod: this.externalStorage?.get('externalRetentionPeriod')?.value }),
                ...(this.externalStorage?.get('backupStorage')?.value !== '' && { backupStorage: this.externalStorage?.get('backupStorage')?.value }),
                ...(this.externalStorage?.get('backupAlerts')?.value !== '' && { backupAlerts: this.externalStorage?.get('backupAlerts')?.value }),
                ...(this.externalStorage?.get('backupEventTypes')?.value !== '' && { backupEventTypes: this.externalStorage?.get('backupEventTypes')?.value }),
                ...(this.externalStorage?.get('footageSchedule')?.value && { footageSchedule: this.externalStorage?.get('footageSchedule')?.value }),
                ...(this.externalStorage?.get('uploadSchedule')?.value && { uploadSchedule: this.externalStorage?.get('uploadSchedule')?.value }),
                ...(this.externalStorage?.get('externalStorageId')?.value && { externalStorageId: this.externalStorage?.get('externalStorageId')?.value }),
              },
            }),
            ...(this.storage?.get('additionalEnabled')?.value !== '' && { additionalEnabled: this.storage?.get('additionalEnabled')?.value }),
          };
        } else {
          this.updatedStorage = {
            storageEnabled: this.storage?.get('storageEnabled')?.value ?? true,
            retentionDays: this.storage?.get('retentionDays')?.value,
            snapshotRetentionDays: this.storage?.get('snapshotRetentionDays')?.value,
            storageMaxDays: this.storage?.get('storageMaxDays')?.value,
            storageMaxGB: this.storage?.get('storageMaxGB')?.value,
            storageSchedule: this.storage?.get('storageSchedule')?.value ?? [],
            smartStorage: {
              enabled: this.smartEnabled,
              preEventDuration: this.storage?.get('preEventDuration')?.value ?? 10,
              postEventDuration: this.storage?.get('postEventDuration')?.value ?? 10,
            },
            storageStream: {
              storageFromSubstream: this.storage?.get('storageStream')?.value === '0' ? false : true,
              storageSubstreamIndex: this.storage?.get('storageStream')?.value === '0' ? 0 : this.storage?.get('storageStream')?.value - 1,
            },
            subStreams: this.subStreams,
            cloudStorage: {
              enabled: this.cloudEnabled ?? false,
              cloudRetentionPeriod: this.cloudStorage?.get('cloudRetentionPeriod')?.value ?? 30,
              backupStorage: this.cloudStorage?.get('backupStorage')?.value ?? true,
              backupAlerts: this.cloudStorage?.get('backupAlerts')?.value ?? true,
              backupEventTypes: this.cloudStorage?.get('backupEventTypes')?.value ?? false,
              footageSchedule: this.cloudStorage?.get('footageSchedule')?.value ?? null,
              uploadSchedule: this.cloudStorage?.get('uploadSchedule')?.value ?? null,
            },
            externalStorage: {
              enabled: this.externalEnabled ?? false,
              externalRetentionPeriod: this.externalStorage?.get('externalRetentionPeriod')?.value ?? 30,
              backupStorage: this.externalStorage?.get('backupStorage')?.value ?? true,
              backupAlerts: this.externalStorage?.get('backupAlerts')?.value ?? true,
              backupEventTypes: this.externalStorage?.get('backupEventTypes')?.value ?? false,
              footageSchedule: this.externalStorage?.get('footageSchedule')?.value ?? null,
              uploadSchedule: this.externalStorage?.get('uploadSchedule')?.value ?? null,
              externalStorageId: this.externalStorage?.get('externalStorageId')?.value ?? null,
            },
            additionalEnabled: this.storage?.get('additionalEnabled').value ?? false,
          };
        }
        this.storage.markAsPristine();
        if (this.standalone) {
          this.onStorageSettingsChange.emit(null);
        }
      });

    this.storage?.get('preEventDuration')
      .valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        if (val < 1) {
          this.storage.get('preEventDuration')
            .setValue(1);
        }
        if (val > 30) {
          this.storage.get('preEventDuration')
            .setValue(30);
        }
      });

    this.storage?.get('postEventDuration')
      .valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        if (val < 1) {
          this.storage.get('postEventDuration')
            .setValue(1);
        }
        if (val > 30) {
          this.storage.get('postEventDuration')
            .setValue(30);
        }
      });

    this.editCameraEffects.pressSave$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updatedStorage.cloudStorage.enabled = this.cloudEnabled ?? false;
        this.store$.dispatch(
          SearchConfigurationActions.cameraStorageUpdate({
            data: this.updatedStorage,
          }),
        );
      });
  }
}
